import { MRT_ColumnDef, MantineReactTable } from 'mantine-react-table';
import 'mantine-react-table/styles.css';
import { FC, ReactNode, useCallback, useContext, useMemo, useState } from 'react';

import { Badge, Button, Group } from '@mantine/core';
import '@mantine/core/styles.css';

import { useQuery } from '@tanstack/react-query';

import { AppContext } from '@/contexts/AppContext/AppContextProvider';

import { ObjectForm } from '@/entities/Objects';
import { ObjectsActions } from '@/entities/Objects/ObjectsActions.tsx';

import { bookingObjectsApi } from '@/shared/api/bookingObjects.ts';
import { IObject } from '@/shared/types/bookingObjects.types.ts';
import { LayoutRow } from '@/shared/uikit/layouts/LayoutRow';

export interface ObjectsDataType {
	key: ReactNode;
	item: IObject;
	name: string;
	id: number;
	type: string;
	description: string;
	order: number | null;
	subRows?: ObjectsDataType[] | null;
}

const Objects: FC = () => {
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [object, setObject] = useState<IObject | null>(null);

	const { admin } = useContext(AppContext);

	const { data: allObjects = [], isLoading: isAllObjectsLoading } = useQuery({
		queryKey: ['objects'],
		queryFn: bookingObjectsApi.getAllObjects,
	});
	const { data: locationTypes, isLoading: isObjectTypeLoading } = useQuery({
		queryKey: ['objectTypes'],
		queryFn: bookingObjectsApi.getObjectsTypes,
	});

	const locationTypesVocabulary = useMemo(() => {
		const obj: {
			[key: string]: string;
		} = {};

		if (locationTypes) {
			locationTypes?.objects.forEach((elem) => {
				obj[elem.id] = elem.name;
			});
		}

		return obj;
	}, [locationTypes]);

	const getData: (next?: number) => ObjectsDataType[] | undefined = useCallback(
		(next = 0) =>
			allObjects
				?.filter((item) => item.parent_id === next && item.id !== 0)
				.map((item) => {
					const { name, description, id, type_id } = item;

					return {
						key: id,
						id,
						item,
						name,
						type: locationTypesVocabulary[type_id],
						subRows: getData(id)?.length ? getData(id) : null,
						description,
						order: item.order_column ?? null,
					};
				}),
		[allObjects],
	);

	const dataSource = useMemo(() => getData(), [getData]);

	const columns: MRT_ColumnDef<ObjectsDataType>[] = [
		{
			accessorKey: 'name',
			header: 'Название',
		},
		{
			accessorKey: 'description',
			header: 'Описание',
		},
		{
			accessorKey: 'type',
			header: 'Тип',
			Cell: ({ cell }) => <Badge variant="light">{`${cell.getValue<string>()}`}</Badge>,
			size: 120,
		},
		{
			accessorKey: 'order',
			header: 'Порядок',
			Cell: ({ cell }) => cell.getValue<number | null>() ?? '—',
			size: 100,
		},
		{
			accessorKey: 'id',
			header: 'ID',
			Cell: ({ cell }) => (
				<Badge color="blue" variant="light">{`${cell.getValue<number>()}`}</Badge>
			),
		},
		{
			accessorKey: 'item',
			header: 'Файлы',

			Cell: ({ cell }) => {
				const value = cell.getValue<IObject>();

				return (
					<>
						{value.svg && (
							<Badge color="cyan" variant="light">
								SVG
							</Badge>
						)}
					</>
				);
			},
		},
	];

	const handleOpen = () => {
		setObject(null);
		setIsModalOpen(true);
	};

	const handleEdit = (item: IObject) => {
		setObject(item);
		setIsModalOpen(true);
	};

	const handleClose = () => {
		setObject(null);
		setIsModalOpen(false);
	};

	return (
		<>
			<LayoutRow
				title={{
					title: { children: 'Объекты', size: 'h4', fw: 800 },
				}}
				type="once"
				style={{ height: 'calc(100dvh - 116px)', margin: '0 16px' }}
			>
				{admin && (
					<Group mb={16}>
						<Button variant="filled" radius="lg" onClick={handleOpen}>
							Создать узел
						</Button>
					</Group>
				)}
				<MantineReactTable
					columns={columns}
					enableStickyHeader
					enableRowVirtualization
					enablePagination={false}
					enableTopToolbar={false}
					enableBottomToolbar={false}
					enableSorting={false}
					enableFilters={false}
					enableHiding={false}
					enableColumnActions={false}
					data={dataSource ?? []}
					positionActionsColumn="last"
					state={{
						isLoading: isAllObjectsLoading || isObjectTypeLoading,
					}}
					mantinePaperProps={{
						shadow: undefined,
					}}
					mantineTableContainerProps={{
						style: {
							maxHeight: '100%',
						},
					}}
					mantineTableHeadCellProps={{
						style: {
							fontSize: 14,
						},
					}}
					mantineTableBodyCellProps={{
						style: {
							fontSize: 14,
						},
					}}
					localization={{
						actions: 'Действия',
						expand: 'Раскрыть',
						expandAll: 'Раскрыть всё',
						collapse: 'Закрыть',
						collapseAll: 'Закрыть всё',
						noRecordsToDisplay: 'Нет записей',
					}}
					displayColumnDefOptions={{
						'mrt-row-actions': {
							size: 120,
						},
					}}
					renderRowActions={({ row }) => <ObjectsActions row={row} handleEdit={handleEdit} />}
					enableRowActions
					enableExpanding
				/>
			</LayoutRow>

			<ObjectForm
				isOpen={isModalOpen}
				object={object}
				objects={allObjects}
				setIsOpen={setIsModalOpen}
				handleClose={handleClose}
			/>
		</>
	);
};

export default Objects;
